import { Chat as ChatIcon, Visibility as ViewIcon, Work as WorkIcon } from "@mui/icons-material";
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Paper, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tutorials } from "../config/tutorials";
import { usePageTutorial } from "../hooks/useTutorial";
import { CoachPlan, coachService } from "../services/CoachService";
import { AgentAvatar } from "./AgentAvatar";
import { CoachPlanModal } from "./CoachPlanModal";
import { HelpIcon } from "./HelpIcon";

// All Coach Types
const allCoaches = [
  {
    id: "sales-coach",
    name: "Don",
    type: "SalesCoachAgent",
    userFriendlyType: "Sales Coach",
    description: "Guidance to help you boost your sales skills and pipeline management.",
    avatar: { initials: "D", color: "#1976d2" },
    specialties: [
      { label: "Sales Strategy", color: "#1976d2" },
      { label: "Pipeline Health", color: "#388e3c" },
      { label: "Win More Deals", color: "#6d4c41" },
    ],
    focus: "Review your sales journey, celebrate wins, and find ways to grow your pipeline using BANT and MEDDIC frameworks.",
    sessionType: "Sales Review Session",
    bgColor: "#f5f7fa",
    accent: "#1976d2",
  },
  {
    id: "bd-coach",
    name: "Taylor",
    type: "BusinessDevelopmentCoachAgent",
    userFriendlyType: "Business Development Coach",
    description: "Support for finding new opportunities and growing your business through strategic partnerships.",
    avatar: { initials: "T", color: "#00838f" },
    specialties: [
      { label: "Business Growth", color: "#00838f" },
      { label: "Lead Generation", color: "#8bc34a" },
      { label: "Market Expansion", color: "#5d4037" },
    ],
    focus: "Explore your business pipeline and brainstorm ways to unlock new growth using TOFU/MOFU/BOFU strategies.",
    sessionType: "BD Review Session",
    bgColor: "#f5f7fa",
    accent: "#00838f",
  },
  {
    id: "product-coach",
    name: "Olivia",
    type: "ProductCoachAgent",
    userFriendlyType: "Product Coach",
    description: "Expert guidance on product strategy, user experience, and product-market fit optimization.",
    avatar: { initials: "O", color: "#ff6f00" },
    specialties: [
      { label: "Product Strategy", color: "#ff6f00" },
      { label: "User Experience", color: "#7b1fa2" },
      { label: "Product-Market Fit", color: "#388e3c" },
    ],
    focus: "Review your product strategy, analyze user feedback, and optimize for better product-market fit.",
    sessionType: "Product Review Session",
    bgColor: "#f5f7fa",
    accent: "#ff6f00",
  },
  {
    id: "marketing-coach",
    name: "Rachel",
    type: "MarketingCoachAgent",
    userFriendlyType: "Marketing Coach",
    description: "Strategic guidance for digital marketing, brand positioning, and customer acquisition.",
    avatar: { initials: "R", color: "#e91e63" },
    specialties: [
      { label: "Digital Marketing", color: "#e91e63" },
      { label: "Brand Positioning", color: "#9c27b0" },
      { label: "Customer Acquisition", color: "#f57c00" },
    ],
    focus: "Review your marketing strategies, analyze campaign performance, and optimize customer acquisition channels.",
    sessionType: "Marketing Review Session",
    bgColor: "#f5f7fa",
    accent: "#e91e63",
  },
  {
    id: "operations-coach",
    name: "Miranda",
    type: "OperationsCoachAgent",
    userFriendlyType: "Operations Coach",
    description: "Expert guidance on process optimization, team efficiency, and operational excellence.",
    avatar: { initials: "M", color: "#424242" },
    specialties: [
      { label: "Process Optimization", color: "#424242" },
      { label: "Team Efficiency", color: "#607d8b" },
      { label: "Operational Excellence", color: "#795548" },
    ],
    focus: "Review your operational processes, identify bottlenecks, and optimize for better efficiency and scalability.",
    sessionType: "Operations Review Session",
    bgColor: "#f5f7fa",
    accent: "#424242",
  },
];

export const CoachesPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableCoaches, setAvailableCoaches] = useState<typeof allCoaches>([]);
  const [coachSummaries, setCoachSummaries] = useState<Array<{ coachType: string; hasActiveChat: boolean; agentId?: string; currentPlan?: any }>>([]);
  const [listLoading, setListLoading] = useState<boolean>(true);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<CoachPlan | null>(null);
  const [selectedCoachName, setSelectedCoachName] = useState<string>("");
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressStep, setProgressStep] = useState(0);
  const [progressText, setProgressText] = useState("");
  const [currentCoach, setCurrentCoach] = useState<string>("");

  // Initialize tutorial for this page
  usePageTutorial("coaches_page_tour", tutorials.coaches);

  useEffect(() => {
    // Load accessible coaches and active chat summaries; don't render list until filtered
    (async () => {
      setListLoading(true);
      try {
        const { accessibleCoachTypes, coaches } = await coachService.getUserCoaches();
        const accessibleSet = new Set(accessibleCoachTypes || []);
        setAvailableCoaches(allCoaches.filter((c) => accessibleSet.has(c.type)));
        setCoachSummaries(coaches || []);
      } catch (err) {
        console.error("Failed to load user coaches", err);
        setAvailableCoaches([]);
        setCoachSummaries([]);
      } finally {
        setListLoading(false);
      }
    })();
  }, []);

  const handleCoachClick = async (coach: (typeof allCoaches)[0]) => {
    setLoading(true);
    setError(null);
    setCurrentCoach(coach.name);

    // Check if coach already has an active session (agent created)
    const isActive = isCoachActive(coach.type);

    if (isActive) {
      // If coach is already active (has agent), go straight to chat without progress animation
      try {
        const coachData = await coachService.getCoach(coach.type);
        console.log("Continuing existing session with:", coachData);
        navigate(`/chat/${coachData.agentId}`);
      } catch (error) {
        console.error("Error continuing coach session:", error);
        setError(error instanceof Error ? error.message : "Failed to continue coach session");
      } finally {
        setLoading(false);
      }
      return;
    }

    // Start the fun progress animation for new sessions
    setProgressOpen(true);
    setProgressStep(0);

    const progressMessages = [
      `☕ Caffeinating ${coach.name}...`,
      `📝 Taking notes and preparing strategies...`,
      `🔍 Searching through knowledge sources...`,
      `💡 Analyzing your data and goals...`,
      `🎯 Setting up personalized coaching plan...`,
      `✨ Almost ready! ${coach.name} is getting excited...`,
    ];

    // Set initial message
    setProgressText(progressMessages[0]);

    // Create coach first, then show progress animation
    let progressInterval: NodeJS.Timeout | null = null;
    try {
      console.log(`Starting ${coach.sessionType} with ${coach.name}`);

      // Step 1: Check if coach already exists
      let coachData;
      try {
        coachData = await coachService.getCoach(coach.type);
        console.log("Coach exists:", coachData);
      } catch (error) {
        console.log("Coach does not exist, will create new one");
        coachData = null;
      }

      // Step 2: Create coach if it doesn't exist
      if (!coachData || !coachData.exists) {
        console.log("Creating new coach...");
        coachData = await coachService.createCoach(coach.type, coach.name);
        console.log("Created coach:", coachData);
      }

      // Record agent id for navigation
      const agentId = coachData.agentId;

      // Step 3: Start review session (fire-and-forget)
      console.log("Starting review session...");
      coachService
        .startReviewSession(coach.type)
        .then((sessionData) => {
          console.log("Started review session:", sessionData);
        })
        .catch((err) => {
          console.error("Failed to start review session:", err);
        });

      // Animate through progress messages, then navigate
      let currentStep = 0;
      progressInterval = setInterval(() => {
        currentStep++;
        if (currentStep < progressMessages.length) {
          setProgressStep(currentStep);
          setProgressText(progressMessages[currentStep]);
        } else {
          if (progressInterval) {
            clearInterval(progressInterval);
          }
          setProgressOpen(false);
          setLoading(false);
          // Navigate to chat after progress animation completes
          navigate(`/chat/${agentId}`);
        }
      }, 1000);
    } catch (error) {
      console.error("Error starting coach session:", error);
      setError(error instanceof Error ? error.message : "Failed to start coach session");
      setProgressOpen(false);
      setLoading(false);
      if (progressInterval) {
        clearInterval(progressInterval);
      }
    }
  };

  const isCoachActive = (coachType: string) => {
    const summary = coachSummaries.find((c) => c.coachType === coachType);
    const result = !!summary?.hasActiveChat;
    return result;
  };

  const handleViewPlan = async (coach: (typeof allCoaches)[0]) => {
    try {
      // Find the agentId from summaries
      const summary = coachSummaries.find((c) => c.coachType === coach.type);
      if (!summary || !summary.agentId) {
        setError("Coach not found or not initialized");
        return;
      }
      // Prefer plan from summary to avoid extra API call
      const planFromSummary = summary.currentPlan;
      if (planFromSummary) {
        setSelectedPlan(planFromSummary);
        setSelectedCoachName(coach.name);
        setPlanModalOpen(true);
      } else {
        // Fallback: attempt to fetch current plan once (optional)
        const plan = await coachService.getCurrentPlan(summary.agentId);
        if (plan) {
          setSelectedPlan(plan);
          setSelectedCoachName(coach.name);
          setPlanModalOpen(true);
        } else {
          setError("No plan found for this coach");
        }
      }
    } catch (error) {
      console.error("Error loading plan:", error);
      setError("Failed to load coach plan");
    }
  };

  const hasPlan = (coachType: string) => {
    const summary = coachSummaries.find((c) => c.coachType === coachType);
    return !!summary?.currentPlan; // Only show plan if backend attached one
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 4, bgcolor: "#f5f7fa" }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <WorkIcon sx={{ mr: 2, fontSize: 32, color: "#1976d2" }} />
          <Typography variant="h4" component="h1" sx={{ color: "#263238" }}>
            Coaching Sessions
          </Typography>
          <Box sx={{ ml: "auto" }}>
            <HelpIcon
              tutorialConfig={tutorials.coaches}
              pageDescription="Work with AI coaches to improve your business performance! Each coach specializes in different areas like sales, marketing, and operations. Start coaching sessions to get personalized guidance."
            />
          </Box>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Start a focused review session with a specialized coach to help you optimize your business performance.
        </Typography>
      </Paper>

      {/* Error Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <Paper sx={{ p: 4, borderRadius: 2, textAlign: "center" }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="h6">Starting your coaching session...</Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait while we set up your session.
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Coaches Grid (show spinner until filtered list ready) */}
      {listLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
            gap: 3,
          }}
          data-tutorial="coaches-list"
        >
          {availableCoaches.map((coach) => {
            const isActive = isCoachActive(coach.type);

            // Debug logging
            console.log(`Coach ${coach.name}: isActive=${isActive}`);

            return (
              <Card
                key={coach.id}
                sx={{
                  height: "100%",
                  cursor: "pointer",
                  borderRadius: 4,
                  bgcolor: coach.bgColor,
                  border: `1.5px solid ${isActive ? "#4caf50" : coach.accent}`,
                  boxShadow: 1,
                  transition: "transform 0.2s, box-shadow 0.2s",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 4,
                    borderColor: isActive ? "#4caf50" : coach.accent,
                  },
                }}
                onClick={() => !loading && handleCoachClick(coach)}
              >
                <CardContent sx={{ pb: 0 }}>
                  {/* Coach Header */}
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <AgentAvatar
                      avatarPath={`/assets/avatars/${coach.name.toLowerCase()}_agent_avatar.png`}
                      name={coach.name}
                      size={140}
                      sx={{
                        width: 140,
                        height: 140,
                        mr: 3,
                        flexShrink: 0,
                        fontSize: "3rem",
                        fontWeight: 600,
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h5" component="h2" sx={{ color: coach.accent, mb: 0.5 }}>
                            {coach.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                            {coach.userFriendlyType}
                          </Typography>
                        </Box>
                        {isActive && (
                          <Chip
                            label="Active"
                            size="small"
                            sx={{
                              ml: 1,
                              bgcolor: "#4caf50",
                              color: "#fff",
                              fontSize: 12,
                            }}
                          />
                        )}
                      </Box>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                        {coach.description}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
                {/* Specialties Pills Row */}
                <Box
                  sx={{
                    px: 2,
                    pb: 2,
                    pt: 0,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    justifyContent: "center",
                  }}
                >
                  {coach.specialties.map((spec, idx) => (
                    <Chip
                      key={idx}
                      label={spec.label}
                      sx={{
                        bgcolor: spec.color,
                        color: "#fff",
                        fontWeight: 500,
                        fontSize: 15,
                        borderRadius: 2,
                      }}
                    />
                  ))}
                </Box>
                {/* Action Buttons */}
                <Box
                  sx={{
                    px: 2,
                    pb: 2,
                    pt: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {/* View Plan Button (if plan exists) */}
                  {hasPlan(coach.type) && (
                    <Button
                      variant="outlined"
                      size="medium"
                      fullWidth
                      disabled={loading}
                      sx={{
                        borderRadius: 2,
                        fontWeight: 500,
                        borderColor: coach.accent,
                        color: coach.accent,
                        "&:hover": {
                          borderColor: coach.accent,
                          backgroundColor: `${coach.accent}10`,
                        },
                      }}
                      startIcon={<ViewIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!loading) {
                          handleViewPlan(coach);
                        }
                      }}
                    >
                      View Plan
                    </Button>
                  )}

                  {/* Start Session Button */}
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    disabled={loading}
                    sx={{
                      borderRadius: 3,
                      fontWeight: 600,
                      fontSize: 18,
                      bgcolor: coach.accent,
                      "&:hover": { bgcolor: coach.accent },
                      "&:disabled": { opacity: 0.7 },
                    }}
                    startIcon={<ChatIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!loading) {
                        handleCoachClick(coach);
                      }
                    }}
                  >
                    {isActive ? "Continue Session" : `Start ${coach.sessionType}`}
                  </Button>
                </Box>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Coach Plan Modal */}
      <CoachPlanModal open={planModalOpen} onClose={() => setPlanModalOpen(false)} plan={selectedPlan} coachName={selectedCoachName} />

      {/* Progress Dialog */}
      {progressOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <Card
            sx={{
              p: 4,
              width: 400,
              minHeight: 300,
              textAlign: "center",
              bgcolor: "white",
              borderRadius: 3,
            }}
          >
            <CircularProgress size={60} sx={{ mb: 3, color: "primary.main" }} />
            <Typography variant="h5" sx={{ mb: 2, fontWeight: "bold" }}>
              Getting {currentCoach} Ready!
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: "text.secondary", minHeight: 24 }}>
              {progressText || "Initializing coaching session..."}
            </Typography>
            <Box sx={{ width: "100%", bgcolor: "grey.200", borderRadius: 1, height: 8, mb: 2 }}>
              <Box
                sx={{
                  width: `${((progressStep + 1) / 6) * 100}%`,
                  bgcolor: "primary.main",
                  height: "100%",
                  borderRadius: 1,
                  transition: "width 0.5s ease-in-out",
                }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ minHeight: 20 }}>
              Step {progressStep + 1} of 6
            </Typography>
          </Card>
        </Box>
      )}
    </Box>
  );
};
